import React from 'react'

import { useStore } from '@/src/types/store'
import { sanitizeExternalUrl } from '@/src/utils/safeUrl'

/**
 * Shown when the landing page itself throws during render.
 *
 * For Essentials and Intelligence tenancies the landing page is the whole
 * public web presence, so the failure mode this replaces is a blank document
 * on a listed company's root domain.
 *
 * Deliberately minimal: no share price, no logo, no theme-derived colours,
 * no data formatting — the things most likely to have caused the throw. It
 * reads two plain strings from the store and renders static markup, so it
 * stays standing even when the richer page cannot.
 */
const LandingPageFallback = () => {
  const businessName = useStore(s => s.homeTheme?.business_name)
  const rawWebsite = useStore(s => s.homeTheme?.corporate_home_url)
  const websiteUrl = sanitizeExternalUrl(rawWebsite)

  return (
    <div className="min-h-[100dvh] bg-[#f5f5f8] flex flex-col items-center justify-center px-6 text-center">
      <h1 className="text-[1.5rem] font-semibold text-[#000000DE] m-0">
        {businessName ? `${businessName} investor portal` : 'Investor portal'}
      </h1>
      <p className="text-[#00000099] mt-3 max-w-[32rem]">
        This page could not be displayed. Please try again shortly.
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
        {websiteUrl && (
          <a
            href={websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-[#07004D]"
          >
            Visit our website
          </a>
        )}
        <a href="/login" className="underline text-[#07004D]">
          Staff sign in
        </a>
      </div>
    </div>
  )
}

export default LandingPageFallback
